import React, {FC, useEffect, useRef} from "react";
import {Field, withFormik} from "formik";
import {t} from "i18next";
import {Button, Input, Select} from "../../commons";
import Validations from "../FormReserveEdit/Validations";
import {useAppSelector} from "../../../redux/hooks";
import {createSelector} from "reselect";
import type { RootState } from 'redux/store';
import store from "redux/store";
import {
    cancelVacantReserves,
    IEditingWarehouseReserve, setIsNeedClearReserveEditForm,
    writeOffVacantReserves
} from "../../../redux/stocksReserve/reducer";
import Textarea from "../../commons/Input/Textarea";
import Radio from "../../commons/Input/Radio";

interface DeliveryFormFields extends IEditingWarehouseReserve {
    actionType: string|null
}

interface DeliveryFormProps {
    values: DeliveryFormFields,
    touched: any,
    errors: any,
    isSubmitting: any,
    handleChange: any,
    handleBlur: any,
    handleSubmit: any,
    resetForm: any,
    toCloseModal: (e:any) => void,
    setFieldValue: any,
}

const initialFields: DeliveryFormFields = {
    sellerID: 0,
    productID: 0,
    warehouseID: '',
    data: {
        total: 0,
        linked: 0,
        cancellingWait: 0,
        writeOffWait: 0
    },
    actionType: null,
    editQ: 0,
    reasonDescription: ''
}

const memoizedSelector = createSelector(
    (state: RootState) => state.sellers.currentSeller,
    (state: RootState) => state.stocksReserve.editingWarehouseReserve?.warehouseID,
    (state: RootState) => state.stocksReserve.editingWarehouseReserve?.productID,
    (state: RootState) => state.stocksReserve.editingWarehouseReserve?.data,
    (state: RootState) => state.stocksReserve.isNeedClearReserveEditForm,
    (currentSeller, warehouseID, productID, data, isNeedClearReserveEditForm) => ({ currentSeller, warehouseID, productID, data, isNeedClearReserveEditForm })
);

const s = createSelector(
    (state: RootState) => state.stocksReserve.isNeedClearReserveEditForm,
    (isNeedClearReserveEditForm) => ({ isNeedClearReserveEditForm })
);

const InnerFormDelivery: FC<DeliveryFormProps> = ({
        values,
        touched,
        errors,
        isSubmitting,
        handleChange,
        handleBlur,
        handleSubmit,
        resetForm,
        setFieldValue,
    }) => {

    const { currentSeller, warehouseID, productID, data } = useAppSelector(memoizedSelector)
    const { isNeedClearReserveEditForm } = useAppSelector(s)

    // @ts-ignore
    values.sellerID = currentSeller.info.data.id
    // @ts-ignore
    values.warehouseID = warehouseID
    // @ts-ignore
    values.productID = productID

    useEffect(() => {
        if (isNeedClearReserveEditForm) {
            resetForm()
        }
    }, [isNeedClearReserveEditForm]);

    useEffect(() => {
        store.dispatch(setIsNeedClearReserveEditForm(false))
    }, [values])

    useEffect(() => {
        const vacantRQ = ((data?.total ?? 0) - (data?.linked ?? 0))
        if (values.editQ > vacantRQ) {
            values.editQ = vacantRQ
        }
    }, [values.editQ])

    const customInputClassName = 'border-[#E2E8F0] mb-1 px-3 py-2 rounded-md text-sm shadow-sm placeholder-slate-400  w-full focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none'
    return (
        <form className="w-full">
            <div><span>по продукту:</span> <span>{ productID }</span></div>
            <div><span>на складе:</span> <span>{ warehouseID }</span></div>
            <div className={`mt-3`}><span>{ data?.total }</span> <span>резервов всего, из которых</span></div>
            <div><span>{ data?.linked }</span> <span>подтверждены заказами</span></div>

            <div className={`mt-3`}>
                <label>
                    <div className='text-sm block text-gray-600 text-left font-bold mb-2'>Списать неподтвержденные резервы:</div>
                </label>
            </div>

            <div>
                <Input
                    type={`number`}
                    min={1}
                    max={((data?.total ?? 0) - (data?.linked ?? 0))}
                    id="editQ"
                    name="editQ"
                    value={values.editQ ? values.editQ.toString() : ''}
                    label={`Из ` + ((data?.total ?? 0) - (data?.linked ?? 0))}
                    changeHandler={handleChange}
                    customClassName={customInputClassName}
                    customLabelClassName="text-sm"
                    blurHandler={handleBlur}
                    validationError={touched.editQ && errors && errors.editQ && errors.editQ.length > 0}
                    validationErrorDescription={touched.editQ ? errors.editQ : ''}
                    require={false}
                />
            </div>
            <div>
                <Textarea
                    id="reasonDescription"
                    name="reasonDescription"
                    value={values.reasonDescription}
                    label={"По причине:"}
                    changeHandler={handleChange}
                    customClassName={customInputClassName}
                    customLabelClassName="text-sm"
                    blurHandler={handleBlur}
                    validationError={touched.reasonDescription && errors && errors.reasonDescription && errors.reasonDescription.length > 0}
                    validationErrorDescription={touched.reasonDescription ? errors.reasonDescription : ''}
                    require={false}
                />
            </div>
            <Field component="div" name="actionType" className="text-sm text-gray-600 flex justify-between items-center">
                <div className="text-sm text-gray-600 text-left font-medium flex flex-col justify-between">
                    <label htmlFor="actionType">Способом:</label>
                    <div className={`flex mt-1 items-center`}>
                        <Radio
                            id="actionTypeWriteOff"
                            name="actionTypeWriteOff"
                            value={'writeOff'}
                            label={'уменьшить остаток на складе'}
                            changeHandler={(e) => values.actionType = e.target.value}
                            customLabelClassName="text-sm"
                            blurHandler={handleBlur}
                            checked={values.actionType ==='writeOff'}
                        />
                    </div>
                    <div className={`flex mt-1 items-center`}>
                        <Radio
                            id="actionTypeCancel"
                            name="actionTypeCancel"
                            value={'cancel'}
                            label={'отменить и вернуть в продажу'}
                            changeHandler={(e) => values.actionType = e.target.value}
                            customLabelClassName="text-sm"
                            blurHandler={handleBlur}
                            checked={values.actionType ==='cancel'}
                        />
                    </div>
                </div>
            </Field>

            {errors.name && touched.name && <div id="feedback">{errors.name}</div>}

            <div className={`mt-5`}>
                <Button
                    type="button"
                    appearance="primary"
                    customClassName="mr-4"
                    clickHandler={handleSubmit}
                    loading={isSubmitting}
                >
                    {t<string>("Списать резервы", {ns: "messages"})}
                </Button>
            </div>

        </form>
    )
}
const FormReserveEdit = withFormik({
    enableReinitialize: true,

    mapPropsToValues: () => (initialFields),

    // Custom sync validation
    validate: values => {
        const errors = {};

        const validation = Validations();

        //@ts-ignore
        Object.keys(values).forEach((key) => validation[key] && validation[key](errors, values[key]))


        return errors;
    },

    handleSubmit: (values, { setSubmitting }) => {
        setSubmitting(true);
        setTimeout(() => {
            if (values.actionType === 'writeOff') {
                //@ts-ignore
                store.dispatch(writeOffVacantReserves(values))
            } else if (values.actionType === 'cancel') {
                //@ts-ignore
                store.dispatch(cancelVacantReserves(values))
            }

            setSubmitting(false);
        }, 500)
    },

    displayName: 'BasicForm',
})(InnerFormDelivery);

export default FormReserveEdit;